(() => {
  const ELEMENT_NAME = 'finch-image-configurator';
  const PHOTO_EXTENSIONS = new Set([
    'jpg', 'jpeg', 'jfif', 'pjpeg', 'pjp', 'png', 'apng', 'webp', 'avif', 'gif', 'bmp', 'dib',
    'heic', 'heif', 'heics', 'heifs', 'hif', 'tif', 'tiff',
  ]);
  const HEIC_EXTENSIONS = new Set(['heic', 'heif', 'heics', 'heifs', 'hif']);
  const TIFF_EXTENSIONS = new Set(['tif', 'tiff']);
  const BLOCKED_EXTENSIONS = new Set(['svg', 'svgz', 'ico', 'psd', 'eps', 'ai', 'dng', 'cr2', 'cr3', 'nef', 'arw']);
  const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);
  const TIFF_MIME_TYPES = new Set(['image/tiff', 'image/tif', 'image/x-tiff']);
  const BLOCKED_MIME_TYPES = new Set(['image/svg+xml', 'image/x-icon', 'image/vnd.adobe.photoshop']);
  const MAX_DECODED_PIXELS = 100_000_000;
  const MAX_WORKING_PIXELS = 12_000_000;
  const DISPLAY_MAX_DIMENSION = 1600;
  const MAX_PREVIEW_BASE_COLORS = 50;
  const MOBILE_EDITOR_BREAKPOINT = 749;
  const DEFAULT_OUTPUT_QUALITY = 0.9;
  const CODEC_DECODE_TIMEOUT = 45_000;

  if (customElements.get(ELEMENT_NAME)) return;

  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);
  const nextFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));
  const yieldToBrowser = () => {
    if (globalThis.scheduler?.yield) return globalThis.scheduler.yield();
    return new Promise((resolve) => setTimeout(resolve, 0));
  };
  class FinchImageConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        clearTimeout(this.disconnectedCleanupTimer);
        this.form = document.getElementById(this.dataset.formId || this.config.formId) || null;
        this.bindExternalFormEvents();
        [
          this.productCanvas,
          this.editCanvas,
          ...(this.upsellCanvases || []),
          ...(this.upsellCanvases || []).map((canvas) => canvas.parentElement),
        ]
          .filter(Boolean)
          .forEach((canvas) => this.resizeObserver?.observe(canvas));
        this.scheduleLegacyUploadKitGuards();
        if (this.sourceFile && !this.image && !this.sourceLoading) this.loadSourceFile(this.sourceFile);
        return;
      }
      this.initialized = true;

      this.config = this.readConfig();
      this.settings = this.config.settings || {};
      this.labels = this.config.labels || {};
      this.form = document.getElementById(this.dataset.formId || this.config.formId) || null;
      this.dialog = this.querySelector('[data-fic-dialog]');
      this.sourceInput = this.querySelector('[data-fic-upload-input]');
      this.filePropertyInput = this.querySelector('[data-fic-file-property]');
      this.cropPropertyInput = this.querySelector('[data-fic-crop-property]');

      if (this.filePropertyInput?.required && this.form) {
        this.filePropertyInput.required = false;
        this.filePropertyInput.tabIndex = -1;
        this.filePropertyInput.setAttribute('aria-hidden', 'true');
        this.form.noValidate = true;
      }
      this.productCanvas = this.querySelector('[data-fic-canvas]');
      this.editCanvas = this.querySelector('[data-fic-edit-canvas]');
      this.upsellCanvases = Array.from(this.querySelectorAll('[data-fic-upsell-canvas]'));
      this.previewCanvas = this.querySelector('[data-fic-preview-canvas]');
      this.previewMeta = this.querySelector('[data-fic-preview-meta]');
      this.previewSelection = this.querySelector('[data-fic-preview-selection]');
      this.previewDrills = this.querySelector('[data-fic-preview-drills]');
      this.productView = this.querySelector('[data-fic-product-view]');
      this.wallView = this.querySelector('[data-fic-wall-view]');
      this.wallArt = this.querySelector('[data-fic-wall-art]');
      this.uploadConfirmation = this.querySelector('[data-fic-upload-confirmation]');
      this.uploadPreview = this.querySelector('[data-fic-upload-preview]');
      this.uploadName = this.querySelector('[data-fic-upload-name]');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.lastFocusedElement = null;
      this.pendingSubmitter = null;
      this.image = null;
      this.sourceFile = null;
      this.generatedFile = null;
      this.sourceObjectUrl = '';
      this.wallObjectUrl = '';
      this.wallRenderTimer = 0;
      this.uploadPreviewToken = 0;
      this.disconnectedCleanupTimer = 0;
      this.wallRenderToken = 0;
      this.sourceLoadToken = 0;
      this.sourceLoading = false;
      this.cancelActiveDecode = null;
      this.configurationVersion = 0;
      this.finalizeToken = 0;
      this.previewRenderToken = 0;
      this.previewWorker = null;
      this.previewWorkerReject = null;
      this.lastPreviewMetadata = null;
      this.configureGuideStage = 'size';
      this.pointerState = new Map();
      this.activePointerMode = '';
      this.lastPointerPoint = null;
      this.pinchStart = null;
      this.editorSnapshot = null;
      this.currentStepName = 'intro';
      this.purchaseInFlight = false;
      this.purchaseState = this.createPurchaseState();
      this.state = {
        focusX: 0.5,
        focusY: 0.5,
        zoom: 1,
        rotation: 0,
        flipX: false,
      };

      this.createStatusRegion();
      this.bindEvents();
      this.scheduleLegacyUploadKitGuards();
      this.initializeChoices();
      this.setStep('intro');
      this.updateReadyState(false);
    }

    disconnectedCallback() {
      this.unbindExternalFormEvents();
      (this.legacyUploadKitTimers || []).forEach((timer) => clearTimeout(timer));
      this.restoreLegacyUploadKitForProduct();
      this.resizeObserver?.disconnect();
      clearTimeout(this.wallRenderTimer);
      this.wallRenderToken += 1;
      this.sourceLoadToken += 1;
      this.sourceLoading = false;
      this.cancelSourceDecode();
      this.setBusy(false);
      this.finalizeToken += 1;
      this.previewRenderToken += 1;
      this.cancelPreviewWorker();
      document.body.classList.remove('fic-dialog-open');
      clearTimeout(this.disconnectedCleanupTimer);
      this.disconnectedCleanupTimer = window.setTimeout(() => {
        if (this.isConnected) return;
        this.releaseDecodedImage();
        this.revokeObjectUrl('sourceObjectUrl');
        this.revokeObjectUrl('wallObjectUrl');
      }, 30_000);
    }

    readConfig() {
      try {
        return JSON.parse(this.querySelector('[data-fic-config]')?.textContent || '{}');
      } catch (error) {
        console.error('Afbeeldingsconfigurator: configuratie kon niet worden gelezen.', error);
        return {};
      }
    }

    legacyUploaderTargetsProduct(uploader, productId) {
      return (uploader?.conditions || []).some(
        (condition) =>
          condition?.field === 'product' &&
          (condition.products || []).some((product) => String(product?.object_id || product?.id || '') === productId)
      );
    }

    legacyUploaderWithoutProduct(uploader, productId) {
      let removedProduct = false;
      const conditions = (uploader?.conditions || []).map((condition) => {
        if (condition?.field !== 'product') return condition;
        const products = (condition.products || []).filter(
          (product) => String(product?.object_id || product?.id || '') !== productId
        );
        if (products.length === (condition.products || []).length) return condition;
        removedProduct = true;
        return products.length ? { ...condition, products } : null;
      });
      if (!removedProduct || conditions.some((condition) => condition === null)) return null;
      return { ...uploader, conditions };
    }

    rememberLegacyUploadKitEntry(bucket, value, index) {
      this.legacyUploadKitRemoved ||= {
        uploadKit: globalThis.UploadKit,
        configured: [],
        active: [],
        containers: [],
        nodes: [],
        ids: new Set(),
      };
      const entries = this.legacyUploadKitRemoved[bucket];
      if (!entries.some((entry) => entry.value === value)) entries.push({ value, index });
      const id = String(value?.id || '');
      if (id) this.legacyUploadKitRemoved.ids.add(id);
    }

    removeLegacyUploadKitNode(node) {
      if (!node) return;
      this.legacyUploadKitRemoved ||= {
        uploadKit: globalThis.UploadKit,
        configured: [],
        active: [],
        containers: [],
        nodes: [],
        ids: new Set(),
      };
      if (!this.legacyUploadKitRemoved.nodes.some((entry) => entry.node === node)) {
        this.legacyUploadKitRemoved.nodes.push({ node, parent: node.parentNode, nextSibling: node.nextSibling });
      }
      node.remove?.();
    }

    legacyUploadKitScope() {
      return (
        this.closest('[id^="shopify-section-"]') ||
        this.closest('.shopify-section') ||
        this.closest('finch-product-buybox') ||
        this.parentElement
      );
    }

    legacyUploadKitNodeBelongsToProduct(node, scope = this.legacyUploadKitScope()) {
      if (!node) return false;
      if (scope?.contains(node)) return true;
      const owningForm = node.closest?.('form') ||
        (node.getAttribute?.('form') ? document.getElementById(node.getAttribute('form')) : null);
      return Boolean(this.form && owningForm === this.form);
    }

    restoreLegacyUploadKitList(list, entries) {
      if (!Array.isArray(list)) return list;
      entries
        .slice()
        .sort((left, right) => left.index - right.index)
        .forEach(({ value, index }) => {
          const id = String(value?.id || '');
          const existingIndex = list.findIndex(
            (candidate) => candidate === value || (id && String(candidate?.id || '') === id)
          );
          if (existingIndex >= 0) list.splice(existingIndex, 1);
          list.splice(Math.min(index, list.length), 0, value);
        });
      return list;
    }

    restoreLegacyUploadKitForProduct() {
      const removed = this.legacyUploadKitRemoved;
      if (!removed || removed.uploadKit !== globalThis.UploadKit) return;
      const uploadKit = removed.uploadKit;
      if (uploadKit.config) {
        uploadKit.config.uploaders = this.restoreLegacyUploadKitList(
          Array.isArray(uploadKit.config.uploaders) ? uploadKit.config.uploaders : [],
          removed.configured
        );
      }
      uploadKit.activeUploaders = this.restoreLegacyUploadKitList(
        Array.isArray(uploadKit.activeUploaders) ? uploadKit.activeUploaders : [],
        removed.active
      );
      uploadKit.containers = this.restoreLegacyUploadKitList(
        Array.isArray(uploadKit.containers) ? uploadKit.containers : [],
        removed.containers
      );
      removed.nodes.forEach(({ node, parent, nextSibling }) => {
        if (!node.isConnected && parent?.isConnected) {
          parent.insertBefore(node, nextSibling?.parentNode === parent ? nextSibling : null);
        }
      });
      this.legacyUploadKitRemoved = null;
    }

    disableLegacyUploadKitForProduct() {
      const uploadKit = globalThis.UploadKit;
      const productId = String(this.config?.product?.id || '');
      if (!uploadKit || !productId) return false;

      const configured = Array.isArray(uploadKit.config?.uploaders) ? uploadKit.config.uploaders : [];
      const active = Array.isArray(uploadKit.activeUploaders) ? uploadKit.activeUploaders : [];
      const conflicts = [...configured, ...active].filter((uploader) =>
        this.legacyUploaderTargetsProduct(uploader, productId)
      );
      const conflictIds = new Set(conflicts.map((uploader) => String(uploader?.id || '')).filter(Boolean));
      (this.legacyUploadKitRemoved?.ids || []).forEach((id) => conflictIds.add(id));
      if (!conflictIds.size) return false;
      const productScope = this.legacyUploadKitScope();

      if (uploadKit.config && Array.isArray(uploadKit.config.uploaders)) {
        uploadKit.config.uploaders = configured.flatMap((uploader, index) => {
          if (!this.legacyUploaderTargetsProduct(uploader, productId)) return [uploader];
          this.rememberLegacyUploadKitEntry('configured', uploader, index);
          const safeUploader = this.legacyUploaderWithoutProduct(uploader, productId);
          return safeUploader ? [safeUploader] : [];
        });
      }
      if (Array.isArray(uploadKit.activeUploaders)) {
        uploadKit.activeUploaders = active.filter((uploader, index) => {
          if (!this.legacyUploaderTargetsProduct(uploader, productId)) return true;
          this.rememberLegacyUploadKitEntry('active', uploader, index);
          return false;
        });
      }

      const removeConflictingContainer = (container) => {
        const uploaderNode = container?.matches?.('[data-uploader-id]')
          ? container
          : container?.querySelector?.('[data-uploader-id]');
        if (!conflictIds.has(String(uploaderNode?.dataset?.uploaderId || ''))) return false;
        if (!this.legacyUploadKitNodeBelongsToProduct(uploaderNode || container, productScope)) return false;
        const containerIndex = uploadKit.containers?.indexOf(container) ?? -1;
        if (containerIndex >= 0) this.rememberLegacyUploadKitEntry('containers', container, containerIndex);
        this.removeLegacyUploadKitNode(container);
        return true;
      };

      if (Array.isArray(uploadKit.containers)) {
        uploadKit.containers = uploadKit.containers.filter((container) => !removeConflictingContainer(container));
      }
      productScope?.querySelectorAll('[data-uploader-id]').forEach((node) => {
        if (conflictIds.has(String(node.dataset.uploaderId || ''))) {
          this.removeLegacyUploadKitNode(node.closest('.uploadkit-injected') || node);
        }
      });
      return true;
    }

    scheduleLegacyUploadKitGuards() {
      (this.legacyUploadKitTimers || []).forEach((timer) => clearTimeout(timer));
      this.disableLegacyUploadKitForProduct();
      this.legacyUploadKitTimers = [0, 250, 1000, 3000].map((delay) =>
        setTimeout(() => this.disableLegacyUploadKitForProduct(), delay)
      );
    }

    createStatusRegion() {
      this.status = document.createElement('p');
      this.status.className = 'fic__status';
      this.status.hidden = true;
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.status.setAttribute('aria-atomic', 'true');
      this.dialog?.querySelector('.fic__header')?.insertAdjacentElement('afterend', this.status);
    }

    bindEvents() {
      this.boundClick = (event) => this.handleClick(event);
      this.boundChange = (event) => this.handleChange(event);
      this.boundDialogClose = () => this.handleDialogClose();
      this.boundDialogCancel = () => this.handleDialogCancel();
      this.boundFormSubmit = (event) => this.handleFormSubmit(event);
      this.boundFormData = (event) => this.handleFormData(event);
      this.boundLegacyUploadKitGuard = () => this.disableLegacyUploadKitForProduct();
      this.boundRequired = (event) => this.handleConfiguratorRequired(event);
      this.boundPointerDown = (event) => this.handlePointerDown(event);
      this.boundPointerMove = (event) => this.handlePointerMove(event);
      this.boundPointerUp = (event) => this.handlePointerUp(event);
      this.boundWheel = (event) => this.handleWheel(event);
      this.boundEditorKeydown = (event) => this.handleEditorKeydown(event);

      this.addEventListener('click', this.boundClick);
      this.addEventListener('change', this.boundChange);
      this.dialog?.addEventListener('close', this.boundDialogClose);
      this.dialog?.addEventListener('cancel', this.boundDialogCancel);
      this.bindExternalFormEvents();
      this.addEventListener('finch:configurator-required', this.boundRequired);

      if (this.editCanvas) {
        this.editCanvas.tabIndex = 0;
        this.editCanvas.addEventListener('pointerdown', this.boundPointerDown);
        this.editCanvas.addEventListener('pointermove', this.boundPointerMove);
        this.editCanvas.addEventListener('pointerup', this.boundPointerUp);
        this.editCanvas.addEventListener('pointercancel', this.boundPointerUp);
        this.editCanvas.addEventListener('wheel', this.boundWheel, { passive: false });
        this.editCanvas.addEventListener('keydown', this.boundEditorKeydown);
      }

      this.resizeObserver = new ResizeObserver(() => this.renderVisibleCanvases());
      [
        this.productCanvas,
        this.editCanvas,
        ...this.upsellCanvases,
        ...this.upsellCanvases.map((canvas) => canvas.parentElement),
      ]
        .filter(Boolean)
        .forEach((canvas) => this.resizeObserver.observe(canvas));
    }

    bindExternalFormEvents() {
      if (!this.form || this.externalFormEventsBound) return;
      this.form.addEventListener('submit', this.boundFormSubmit, true);
      this.form.addEventListener('formdata', this.boundFormData);
      this.form.addEventListener('click', this.boundLegacyUploadKitGuard, true);
      this.externalFormEventsBound = true;
    }

    unbindExternalFormEvents() {
      if (!this.form || !this.externalFormEventsBound) return;
      this.form.removeEventListener('submit', this.boundFormSubmit, true);
      this.form.removeEventListener('formdata', this.boundFormData);
      this.form.removeEventListener('click', this.boundLegacyUploadKitGuard, true);
      this.externalFormEventsBound = false;
    }

    initializeChoices() {
      const selectedOrientation = this.querySelector('[data-fic-orientation]:checked');
      this.orientation = selectedOrientation?.dataset.ficOrientation || 'landscape';
      const compatibleSize = this.getSelectedSize();
      if (!this.isSizeCompatible(compatibleSize, this.orientation) || compatibleSize?.disabled) {
        this.selectFirstCompatibleSize(this.orientation);
      }

      this.updateChoiceVisibility();
      this.updateChoiceSummary();
      this.updateVariantSelection(false);
      this.updateCanvasRatios();
      this.showView('product');
    }

    handleClick(event) {
      const control = event.target.closest('[data-fic-action]');
      if (!control || !this.contains(control)) return;

      const action = control.dataset.ficAction;
      const handlers = {
        open: () => this.open(),
        close: () => this.close(),
        back: () => this.setStep('intro'),
        upload: () => this.sourceInput?.click(),
        'show-configure': () => this.showConfigure(),
        'show-edit': () => this.showEditor(),
        'cancel-edit': () => this.cancelEditor(),
        'apply-edit': () => this.applyEditor(),
        'zoom-out': () => this.adjustZoom(-0.15),
        'zoom-in': () => this.adjustZoom(0.15),
        rotate: () => this.rotateImage(),
        flip: () => this.flipImage(),
        fit: () => this.fitImage(),
        'product-view': () => this.showView('product'),
        'wall-view': () => this.showView('wall'),
        'toggle-sizes': () => {
          this.setConfigureGuide('size');
          this.togglePanel('[data-fic-size-panel]', control);
        },
        'toggle-stones': () => {
          this.setConfigureGuide('stone');
          this.togglePanel('[data-fic-stone-panel]', control);
        },
        'show-preview': () => this.showMosaicPreview(),
        continue: () => this.showUpsellExtras(),
        'show-upsell-extras': () => this.showUpsellExtras(),
        'show-upsell-services': () => this.showUpsellServices(),
        'add-another': () => this.commitPurchase('another', control),
        'add-to-cart': () => this.commitPurchase('cart', control),
      };

      if (handlers[action]) {
        event.preventDefault();
        handlers[action]();
      }
    }

    handleChange(event) {
      if (event.target === this.sourceInput) {
        const [file] = this.sourceInput.files || [];
        if (file) this.loadSourceFile(file);
        return;
      }

      if (event.target.matches('[data-fic-orientation]')) {
        this.orientation = event.target.dataset.ficOrientation;
        const selectedSize = this.getSelectedSize();
        if (!this.isSizeCompatible(selectedSize, this.orientation) || selectedSize?.disabled) {
          this.selectFirstCompatibleSize(this.orientation);
        }
        this.markDirty();
        this.updateChoiceVisibility();
        this.updateChoiceSummary();
        this.updateVariantSelection(true);
        this.updateCanvasRatios();
        this.renderVisibleCanvases();
        return;
      }

      if (event.target.matches('[data-fic-size]')) {
        this.markDirty();
        this.updateChoiceSummary();
        this.updateVariantSelection(true);
        this.updateCanvasRatios();
        this.renderVisibleCanvases();
        this.setConfigureGuide('stone');
        this.openMobileChoicePanel('[data-fic-stone-panel]');
        return;
      }

      if (event.target.matches('[data-fic-stone]')) {
        this.markDirty();
        this.updateChoiceSummary();
        this.setConfigureGuide('continue');
        this.collapseMobileChoicePanels();
        return;
      }

      if (event.target.matches('[data-fic-upsell-toggle]')) {
        this.updateUpsellCard(event.target);
      }
    }

    open(options = {}) {
      if (!this.dialog) return;
      if (options.pendingSubmitter) this.pendingSubmitter = options.pendingSubmitter;
      this.lastFocusedElement = document.activeElement;
      const resumeUpsellStep = /^upsell-/.test(this.currentStepName || '')
        ? this.currentStepName
        : null;
      this.setStep(this.image ? resumeUpsellStep || 'configure' : 'intro');
      this.clearStatus();

      if (!this.dialog.open) this.dialog.showModal();
      document.body.classList.add('fic-dialog-open');

      requestAnimationFrame(() => {
        const focusTarget = this.image
          ? this.dialog.querySelector('[data-fic-action="continue"]')
          : this.dialog.querySelector('[data-fic-action="upload"]');
        focusTarget?.focus({ preventScroll: true });
        this.renderVisibleCanvases();
      });
    }

    close() {
      if (this.sourceLoading) {
        this.sourceLoadToken += 1;
        this.sourceLoading = false;
        this.cancelSourceDecode();
        this.setBusy(false);
      }
      this.cancelPendingPurchase();
      if (this.dialog?.open) this.dialog.close();
    }

    handleDialogCancel() {
      if (this.sourceLoading) {
        this.sourceLoadToken += 1;
        this.sourceLoading = false;
        this.cancelSourceDecode();
        this.setBusy(false);
      }
      this.cancelPendingPurchase();
      this.previewRenderToken += 1;
      this.cancelPreviewWorker();
    }

    handleDialogClose() {
      this.cancelPendingPurchase();
      document.body.classList.remove('fic-dialog-open');
      this.previewRenderToken += 1;
      this.cancelPreviewWorker();
      clearTimeout(this.wallRenderTimer);
      const focusTarget = this.lastFocusedElement;
      this.lastFocusedElement = null;
      if (focusTarget instanceof HTMLElement && focusTarget.isConnected) focusTarget.focus({ preventScroll: true });
    }

    cancelPendingPurchase() {
      this.pendingSubmitter = null;
      this.pendingBuyNow = false;
      this.finalizeToken += 1;
    }

    setStep(name) {
      this.currentStepName = name;
      this.querySelectorAll('[data-fic-step]').forEach((step) => {
        step.hidden = step.dataset.ficStep !== name;
      });
      this.dataset.step = name;
      if (name === 'configure') {
        this.collapseMobileChoicePanels();
        this.setConfigureGuide(this.configureGuideStage || 'size');
        if (this.configureGuideStage === 'size') {
          this.openMobileChoicePanel('[data-fic-size-panel]');
        } else if (this.configureGuideStage === 'stone') {
          this.openMobileChoicePanel('[data-fic-stone-panel]');
        }
      }
      this.dialog && (this.dialog.scrollTop = 0);
      const activeStep = this.querySelector(`[data-fic-step="${name}"]`);
      if (activeStep) activeStep.scrollTop = 0;
      if (name === 'configure' || name === 'edit' || /^upsell-/.test(name)) {
        requestAnimationFrame(() => this.renderVisibleCanvases());
      }
      if (/^upsell-/.test(name)) {
        requestAnimationFrame(() => {
          activeStep?.querySelector('[data-fic-step-heading], h3')?.focus({ preventScroll: true });
        });
      }
    }

    setConfigureGuide(stage = 'size') {
      const normalized = ['size', 'stone', 'continue'].includes(stage) ? stage : 'size';
      this.configureGuideStage = normalized;
      this.dataset.guideStage = normalized;
      this.querySelectorAll(
        '[data-fic-action="toggle-sizes"], [data-fic-action="toggle-stones"], [data-fic-action="continue"]'
      ).forEach((control) => control.removeAttribute('aria-current'));

      const selector = normalized === 'size'
        ? '[data-fic-action="toggle-sizes"]'
        : normalized === 'stone'
          ? '[data-fic-action="toggle-stones"]'
          : '[data-fic-action="continue"]';
      this.querySelectorAll(selector).forEach((control) => control.setAttribute('aria-current', 'step'));
    }

    showUpsellExtras() {
      if (!this.validateConfiguration()) return;
      this.clearStatus();
      this.setStep('upsell-extras');
    }

    showUpsellServices() {
      if (!this.validateConfiguration()) return;
      this.clearStatus();
      this.setStep('upsell-services');
    }

    showConfigure() {
      if (!this.image) {
        this.setStep('intro');
        this.showStatus(this.labels.requiredUpload || 'Upload eerst een foto.', true);
        return;
      }
      this.setStep('configure');
      this.clearStatus();
      this.showView(this.currentView || 'product');
    }

    showEditor() {
      if (!this.image) return;
      this.editorSnapshot = { ...this.state };
      this.setStep('edit');
      requestAnimationFrame(() => {
        this.drawToCanvas(this.editCanvas);
        this.editCanvas?.focus({ preventScroll: true });
      });
    }

    cancelEditor() {
      if (this.editorSnapshot) this.state = { ...this.editorSnapshot };
      this.editorSnapshot = null;
      this.showConfigure();
    }

    applyEditor() {
      this.editorSnapshot = null;
      this.markDirty();
      this.showConfigure();
      this.scheduleWallPreview();
    }

    showView(view) {
      this.currentView = view === 'wall' ? 'wall' : 'product';
      if (this.productView) this.productView.hidden = this.currentView !== 'product';
      if (this.wallView) this.wallView.hidden = this.currentView !== 'wall';
      this.querySelectorAll('[data-fic-action="product-view"]').forEach((button) => {
        button.setAttribute('aria-pressed', String(this.currentView === 'product'));
      });
      this.querySelectorAll('[data-fic-action="wall-view"]').forEach((button) => {
        button.setAttribute('aria-pressed', String(this.currentView === 'wall'));
      });

      if (this.currentView === 'wall') this.scheduleWallPreview(0);
      else requestAnimationFrame(() => this.drawToCanvas(this.productCanvas));
    }

    togglePanel(selector, button) {
      const panel = this.querySelector(selector);
      const content = panel?.querySelector('.fic__choice-content');
      if (!content) return;
      const shouldOpen = content.hidden;

      if (this.isMobileEditorLayout() && shouldOpen) {
        this.querySelectorAll('[data-fic-size-panel], [data-fic-stone-panel]').forEach((otherPanel) => {
          if (otherPanel === panel) return;
          const otherContent = otherPanel.querySelector('.fic__choice-content');
          const otherToggle = otherPanel.querySelector('.fic__choice-toggle');
          if (otherContent) otherContent.hidden = true;
          otherToggle?.setAttribute('aria-expanded', 'false');
        });
      }

      content.hidden = !shouldOpen;
      button.setAttribute('aria-expanded', String(shouldOpen));
    }

    openMobileChoicePanel(selector) {
      if (!this.isMobileEditorLayout()) return;
      this.querySelectorAll('[data-fic-size-panel], [data-fic-stone-panel]').forEach((panel) => {
        const content = panel.querySelector('.fic__choice-content');
        const toggle = panel.querySelector('.fic__choice-toggle');
        const shouldOpen = panel.matches(selector);
        if (content) content.hidden = !shouldOpen;
        toggle?.setAttribute('aria-expanded', String(shouldOpen));
      });
    }

    collapseMobileChoicePanels() {
      if (!this.isMobileEditorLayout()) return;
      this.querySelectorAll('[data-fic-size-panel], [data-fic-stone-panel]').forEach((panel) => {
        const content = panel.querySelector('.fic__choice-content');
        const toggle = panel.querySelector('.fic__choice-toggle');
        if (content) content.hidden = true;
        toggle?.setAttribute('aria-expanded', 'false');
      });
    }

    updateUpsellCard(toggle) {
      const card = toggle?.closest('[data-fic-upsell-card]');
      const selected = Boolean(toggle?.checked && !toggle.disabled);
      card?.classList.toggle('is-selected', selected);
      card?.setAttribute('data-selected', String(selected));
    }

    getSelectedUpsellItems() {
      const variantIds = new Set();
      this.querySelectorAll('[data-fic-upsell-toggle]:checked').forEach((toggle) => {
        // setBusy() disables all controls while the cart mutation runs. A checked
        // toggle still represents the customer's committed selection in that state.
        const variantId = Number(toggle.value || toggle.closest('[data-fic-upsell-card]')?.dataset.variantId);
        if (!Number.isSafeInteger(variantId) || variantId <= 0) return;
        variantIds.add(variantId);
      });

      return Array.from(variantIds, (id) => ({ id, quantity: 1 }));
    }

    resetUpsellSelections() {
      this.querySelectorAll('[data-fic-upsell-toggle]').forEach((toggle) => {
        toggle.checked = toggle.dataset.defaultSelected === 'true' && !toggle.disabled;
        this.updateUpsellCard(toggle);
      });
    }

    async loadSourceFile(file) {
      this.cancelSourceDecode();
      const token = ++this.sourceLoadToken;
      this.clearStatus();
      this.sourceInput.value = '';

      const maximumBytes = Number(this.settings.maxFileSizeBytes) || 0;
      if (maximumBytes > 0 && file.size > maximumBytes) {
        this.sourceLoading = false;
        this.setBusy(false);
        this.showStatus(this.labels.fileTooLarge || 'Dit bestand is te groot.', true);
        return;
      }

      this.sourceLoading = true;
      this.setBusy(true, this.labels.processing || 'Afbeelding verwerken…');
      try {
        const sourceKind = await this.identifyPhotoKind(file);
        if (!sourceKind) throw new Error('unsupported-photo-type');
        const rawDecoded = await this.decodeImage(file, sourceKind);
        const decoded = await this.normalizeDecodedImage(rawDecoded);
        if (token !== this.sourceLoadToken) {
          decoded.close?.();
          return;
        }
        const width = Number(decoded.width || decoded.naturalWidth);
        const height = Number(decoded.height || decoded.naturalHeight);
        if (!width || !height) {
          decoded.close?.();
          throw new Error('source-dimensions');
        }

        this.releaseDecodedImage();
        this.revokeObjectUrl('sourceObjectUrl');
        this.image = decoded;
        this.sourceFile = file;
        this.setConfigureGuide('size');
        this.purchaseState = this.createPurchaseState();
        this.resetUpsellSelections();
        this.markDirty();
        this.state = { focusX: 0.5, focusY: 0.5, zoom: 1, rotation: 0, flipX: false };
        this.generatedFile = null;
        this.updateReadyState(false);
        this.editorSnapshot = null;
        this.showConfigure();
        this.updateCanvasRatios();
        await nextFrame();
        this.renderVisibleCanvases();
        await this.updateUploadConfirmation(token);
        this.scheduleWallPreview(0);
      } catch (error) {
        if (token !== this.sourceLoadToken || error?.name === 'AbortError') return;
        console.error('Afbeeldingsconfigurator: afbeelding kon niet worden geladen.', error);
        const message = error?.message === 'unsupported-photo-type'
          ? this.labels.invalidFile
          : error?.message === 'source-too-large'
            ? this.labels.imageTooLarge
            : this.labels.decodeFailed || this.labels.invalidFile;
        this.showStatus(message || 'Deze afbeelding kon niet worden gelezen.', true);
      } finally {
        if (token === this.sourceLoadToken) {
          this.sourceLoading = false;
          this.setBusy(false);
        }
      }
    }

    fileExtension(file) {
      return String(file?.name || '').split('.').pop()?.toLowerCase() || '';
    }

    async identifyPhotoKind(file) {
      const extension = this.fileExtension(file);
      const mimeType = String(file?.type || '').toLowerCase().split(';')[0];
      if (BLOCKED_EXTENSIONS.has(extension) || BLOCKED_MIME_TYPES.has(mimeType)) return null;
      if (HEIC_EXTENSIONS.has(extension) || HEIC_MIME_TYPES.has(mimeType)) return 'heic';
      if (TIFF_EXTENSIONS.has(extension) || TIFF_MIME_TYPES.has(mimeType)) return 'tiff';

      const bytes = new Uint8Array(await file.slice(0, 32).arrayBuffer());
      const ascii = (start, length) => String.fromCharCode(...bytes.slice(start, start + length));
      if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'native';
      if (ascii(0, 8) === '\u0089PNG\r\n\u001a\n') return 'native';
      if (ascii(0, 6) === 'GIF87a' || ascii(0, 6) === 'GIF89a') return 'native';
      if (ascii(0, 2) === 'BM') return 'native';
      if (ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') return 'native';
      if ((ascii(0, 4) === 'II*\u0000') || (ascii(0, 4) === 'MM\u0000*')) return 'tiff';
      if (ascii(4, 4) === 'ftyp') {
        const brand = ascii(8, 4).toLowerCase();
        if (['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1'].includes(brand)) return 'heic';
        if (['avif', 'avis'].includes(brand)) return 'native';
      }

      if (PHOTO_EXTENSIONS.has(extension)) return 'native';
      if (mimeType.startsWith('image/') && !BLOCKED_MIME_TYPES.has(mimeType)) return 'native';
      return null;
    }

    async decodeImage(file, sourceKind = 'native') {
      if (sourceKind === 'heic') return this.decodeHeicImage(file);
      if (sourceKind === 'tiff') return this.decodeTiffImage(file);
      try {
        return await this.decodeNativeImage(file);
      } catch (nativeError) {
        throw nativeError;
      }
    }

    async decodeNativeImage(file) {
      if ('createImageBitmap' in window) {
        try {
          return await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch (error) {
          // Safari versions without this option continue through the Image fallback.
        }
      }

      const objectUrl = URL.createObjectURL(file);
      const image = new Image();
      image.decoding = 'async';
      image.src = objectUrl;
      try {
        await image.decode();
        return image;
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    }

    async decodeHeicImage(file) {
      return this.decodeCodecInWorker(file, this.settings.heicDecoderUrl, 'heic');
    }

    async decodeTiffImage(file) {
      return this.decodeCodecInWorker(file, this.settings.tiffDecoderUrl, 'tiff');
    }

    cancelSourceDecode() {
      const cancel = this.cancelActiveDecode;
      this.cancelActiveDecode = null;
      cancel?.();
    }

    async decodeCodecInWorker(file, url, kind) {
      let lastError;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          return await this.decodeCodecAttempt(file, url, kind);
        } catch (error) {
          lastError = error;
          const retryable = /^codec-(?:timeout|message|worker)-/.test(String(error?.message || ''));
          if (error?.name === 'AbortError' || !retryable || attempt === 1) throw error;
          await yieldToBrowser();
        }
      }
      throw lastError;
    }

    decodeCodecAttempt(file, url, kind) {
      if (!url || typeof Worker === 'undefined') throw new Error(`codec-worker-unavailable-${kind}`);
      let worker;
      try {
        worker = new Worker(url);
      } catch (error) {
        throw new Error(`codec-worker-${kind}: ${error?.message || 'worker kon niet starten'}`);
      }
      const reader = new FileReader();
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return new Promise((resolve, reject) => {
        let settled = false;
        let timeout = 0;
        const finish = (callback, abortRead = false) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          worker.terminate();
          if (this.cancelActiveDecode === cancel) this.cancelActiveDecode = null;
          if (abortRead && reader.readyState === FileReader.LOADING) reader.abort();
          callback();
        };
        const cancel = () => finish(
          () => reject(new DOMException('Decode geannuleerd', 'AbortError')),
          true
        );
        timeout = window.setTimeout(
          () => finish(() => reject(new Error(`codec-timeout-${kind}`)), true),
          CODEC_DECODE_TIMEOUT
        );
        this.cancelActiveDecode = cancel;
        reader.addEventListener('load', () => {
          if (settled) return;
          const buffer = reader.result;
          if (!(buffer instanceof ArrayBuffer)) {
            finish(() => reject(new Error(`codec-read-${kind}`)));
            return;
          }
          worker.postMessage(
            { id, buffer, maxPixels: MAX_DECODED_PIXELS, maxWorkingPixels: MAX_WORKING_PIXELS },
            [buffer]
          );
        });
        reader.addEventListener('error', () =>
          finish(() => reject(new Error(`codec-read-${kind}`)))
        );
        reader.addEventListener('abort', () =>
          finish(() => reject(new DOMException('Decode geannuleerd', 'AbortError')))
        );
        worker.addEventListener('message', (event) => {
          if (event.data?.id !== id) return;
          finish(() => {
            if (!event.data.ok) {
              reject(new Error(event.data.error || `codec-decode-${kind}`));
              return;
            }
            if (event.data.bitmap) {
              resolve(event.data.bitmap);
              return;
            }
            resolve(
              this.imageDataToDrawable({
                width: event.data.width,
                height: event.data.height,
                data: new Uint8ClampedArray(event.data.data),
              })
            );
          });
        });
        worker.addEventListener('messageerror', () =>
          finish(() => reject(new Error(`codec-message-${kind}`)))
        );
        worker.addEventListener('error', (event) =>
          finish(() => reject(new Error(`codec-worker-${kind}: ${event.message || 'workerfout'}`)))
        );
        reader.readAsArrayBuffer(file);
      });
    }

    async imageDataToDrawable(imageData) {
      if (!imageData?.width || !imageData?.height || !imageData?.data) throw new Error('source-dimensions');
      const data = imageData.data instanceof Uint8ClampedArray
        ? imageData.data
        : new Uint8ClampedArray(imageData.data);
      const normalizedImageData = imageData instanceof ImageData
        ? imageData
        : new ImageData(data, imageData.width, imageData.height);
      if ('createImageBitmap' in window) return createImageBitmap(normalizedImageData);
      const canvas = document.createElement('canvas');
      canvas.width = normalizedImageData.width;
      canvas.height = normalizedImageData.height;
      canvas.getContext('2d').putImageData(normalizedImageData, 0, 0);
      return canvas;
    }

    async normalizeDecodedImage(decoded) {
      const width = Number(decoded?.width || decoded?.naturalWidth);
      const height = Number(decoded?.height || decoded?.naturalHeight);
      const pixels = width * height;
      if (!width || !height) throw new Error('source-dimensions');
      if (pixels > MAX_DECODED_PIXELS) {
        decoded.close?.();
        throw new Error('source-too-large');
      }
      if (pixels <= MAX_WORKING_PIXELS) return decoded;

      const scale = Math.sqrt(MAX_WORKING_PIXELS / pixels);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const context = canvas.getContext('2d');
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(decoded, 0, 0, canvas.width, canvas.height);
      decoded.close?.();
      if ('createImageBitmap' in window) return createImageBitmap(canvas);
      return canvas;
    }

    async updateUploadConfirmation(token = this.sourceLoadToken) {
      if (!this.image || !this.uploadConfirmation || !this.uploadPreview) return;
      const previewToken = ++this.uploadPreviewToken;
      const canvas = this.createCropCanvas(420);
      const blob = await this.canvasToBlob(canvas, 'image/jpeg', 0.84);
      if (token !== this.sourceLoadToken || previewToken !== this.uploadPreviewToken) return;
      this.revokeObjectUrl('sourceObjectUrl');
      this.sourceObjectUrl = URL.createObjectURL(blob);
      this.uploadPreview.src = this.sourceObjectUrl;
      if (this.uploadName) this.uploadName.textContent = this.sourceFile?.name || '';
      this.uploadConfirmation.hidden = false;
      this.dataset.hasSource = 'true';
    }

    releaseDecodedImage() {
      if (this.image && typeof this.image.close === 'function') this.image.close();
      this.image = null;
    }

    revokeObjectUrl(propertyName) {
      const url = this[propertyName];
      if (url) URL.revokeObjectURL(url);
      this[propertyName] = '';
    }

    getSelectedSize() {
      return this.querySelector('[data-fic-size]:checked');
    }

    getSelectedStone() {
      return this.querySelector('[data-fic-stone]:checked');
    }

    getSelectedOrientation() {
      return this.querySelector('[data-fic-orientation]:checked');
    }

    getSizeDimensions(input = this.getSelectedSize()) {
      if (!input) return { width: 1, height: 1 };
      let width = Number(input.dataset.width);
      let height = Number(input.dataset.height);
      if (!width || !height) {
        const values = String(input.value).match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i);
        width = Number(values?.[1]?.replace(',', '.'));
        height = Number(values?.[2]?.replace(',', '.'));
      }
      return { width: width || 1, height: height || 1 };
    }

    getCropRatio() {
      const { width, height } = this.getSizeDimensions();
      if (this.orientation === 'square' || width === height) return 1;
      const longSide = Math.max(width, height);
      const shortSide = Math.min(width, height);
      return this.orientation === 'portrait' ? shortSide / longSide : longSide / shortSide;
    }

    isSizeCompatible(input, orientation) {
      if (!input) return false;
      const { width, height } = this.getSizeDimensions(input);
      return orientation === 'square' ? width === height : width !== height;
    }

    selectFirstCompatibleSize(orientation) {
      const sizeInputs = Array.from(this.querySelectorAll('[data-fic-size]'));
      sizeInputs.forEach((input) => {
        input.checked = false;
      });
      const compatible = sizeInputs.find(
        (input) => !input.disabled && this.isSizeCompatible(input, orientation)
      );
      if (compatible) compatible.checked = true;
      return compatible;
    }

    updateChoiceVisibility() {
      this.querySelectorAll('.fic__size-option').forEach((label) => {
        const input = label.querySelector('[data-fic-size]');
        label.hidden = !this.isSizeCompatible(input, this.orientation);
      });
    }

    updateChoiceSummary() {
      const selectedSize = this.getSelectedSize();
      const selectedStone = this.getSelectedStone();
      const currentSize = this.querySelector('[data-fic-current-size]');
      const currentStone = this.querySelector('[data-fic-current-stone]');
      if (currentSize && selectedSize) currentSize.textContent = selectedSize.value;
      if (currentStone && selectedStone) currentStone.textContent = selectedStone.value;
      this.updateSummaryPrice(selectedSize);
    }

    updateSummaryPrice(selectedSize) {
      if (!selectedSize) return;
      const variant = this.getVariantForSize(selectedSize);
      const price = this.querySelector('[data-fic-current-price]');
      const comparePrice = this.querySelector('[data-fic-current-compare-price]');
      if (price && variant?.price) price.textContent = variant.price;
      if (comparePrice) {
        const showCompare = Number(variant?.compareAtPriceValue) > Number(variant?.priceValue);
        comparePrice.hidden = !showCompare;
        comparePrice.textContent = showCompare ? variant.compareAtPrice : '';
      }
    }

    getVariantForSize(input = this.getSelectedSize()) {
      const variantId = String(input?.dataset.variantId || '');
      return (this.config.variants || []).find((variant) => String(variant.id) === variantId) || null;
    }

    updateVariantSelection(announceChange) {
      const selectedSize = this.getSelectedSize();
      if (!selectedSize) {
        const variantInput = this.form?.querySelector('[name="id"]');
        if (variantInput) variantInput.disabled = true;
        return;
      }
      const variantId = selectedSize.dataset.variantId;
      const optionIndex = Number(selectedSize.dataset.optionIndex || 0);

      if (this.form) {
        const variantInput = this.form.querySelector('[name="id"]');
        if (variantInput && variantId) {
          variantInput.value = variantId;
          variantInput.disabled = false;
        }
      }

      const buybox = this.closest('finch-product-buybox');
      const optionInput = buybox?.querySelector(`[data-fpb-option-input][data-option-index="${optionIndex}"]`);
      if (optionInput && optionInput.value !== selectedSize.value) {
        optionInput.value = selectedSize.value;
        if (announceChange) optionInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateCanvasRatios() {
      const ratio = this.getCropRatio();
      [this.productCanvas, this.editCanvas, this.previewCanvas, ...(this.upsellCanvases || [])]
        .filter(Boolean)
        .forEach((canvas) => {
          const displayRatio = canvas === this.editCanvas && this.isMobileEditorLayout()
            ? this.getMobileEditorStageRatio()
            : ratio;
          canvas.style.aspectRatio = String(displayRatio);
          canvas.style.setProperty('--fic-crop-ratio', String(displayRatio));
        });
      if (this.productView) this.productView.style.setProperty('--fic-crop-ratio', String(ratio));
      if (this.wallArt) this.wallArt.style.aspectRatio = String(ratio);
      this.clampFocus();
    }

    isMobileEditorLayout() {
      if (typeof window.matchMedia === 'function') {
        return window.matchMedia(`(max-width: ${MOBILE_EDITOR_BREAKPOINT}px)`).matches;
      }
      return Number(window.innerWidth || 0) <= MOBILE_EDITOR_BREAKPOINT;
    }

    getMobileEditorStageRatio() {
      const viewportWidth = Number(window.visualViewport?.width || window.innerWidth || 390);
      const viewportHeight = Number(window.visualViewport?.height || window.innerHeight || 844);
      const desiredHeight = clamp(viewportHeight * 0.445, 250, 305);
      return clamp(viewportWidth / desiredHeight, 1.22, 1.72);
    }

    syncEditorCanvasLayout(canvas) {
      if (canvas !== this.editCanvas) return;
      const mobile = this.isMobileEditorLayout();
      const ratio = mobile ? this.getMobileEditorStageRatio() : this.getCropRatio();
      canvas.style.aspectRatio = String(ratio);
      canvas.style.setProperty('--fic-crop-ratio', String(ratio));
      canvas.dataset.ficEditorLayout = mobile ? 'mobile-internal-crop' : 'desktop-crop';
      if (!mobile) delete canvas.dataset.ficCropRect;
    }

    renderVisibleCanvases() {
      if (!this.image) return;
      if (this.productCanvas && !this.productCanvas.closest('[hidden]')) this.drawToCanvas(this.productCanvas);
      if (this.editCanvas && !this.editCanvas.closest('[hidden]')) this.drawToCanvas(this.editCanvas);
      (this.upsellCanvases || []).forEach((canvas) => {
        if (!canvas.closest('[hidden]')) this.drawToCanvas(canvas);
      });
    }

    syncUpsellCanvasLayout(canvas, ratio) {
      if (!(this.upsellCanvases || []).includes(canvas)) return;
      const stage = canvas.parentElement;
      if (!stage || stage.clientWidth <= 0 || stage.clientHeight <= 0) return;

      const stageStyles = window.getComputedStyle(stage);
      const horizontalPadding =
        (Number.parseFloat(stageStyles.paddingLeft) || 0) +
        (Number.parseFloat(stageStyles.paddingRight) || 0);
      const verticalPadding =
        (Number.parseFloat(stageStyles.paddingTop) || 0) +
        (Number.parseFloat(stageStyles.paddingBottom) || 0);
      const availableWidth = Math.max(1, stage.clientWidth - horizontalPadding);
      const availableHeight = Math.max(1, stage.clientHeight - verticalPadding);
      const rootFontSize = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 10;
      const preferredWidth = Math.min(availableWidth * 0.82, rootFontSize * 86);
      const displayWidth = Math.max(1, Math.min(preferredWidth, availableHeight * ratio));
      const displayHeight = Math.max(1, displayWidth / ratio);

      canvas.style.width = `${displayWidth.toFixed(3)}px`;
      canvas.style.height = `${displayHeight.toFixed(3)}px`;
      canvas.style.inlineSize = `${displayWidth.toFixed(3)}px`;
      canvas.style.blockSize = `${displayHeight.toFixed(3)}px`;
    }

    prepareDisplayCanvas(canvas) {
      if (!canvas) return null;
      this.syncEditorCanvasLayout(canvas);
      const ratio = canvas === this.editCanvas && this.isMobileEditorLayout()
        ? this.getMobileEditorStageRatio()
        : this.getCropRatio();
      canvas.style.aspectRatio = String(ratio);
      canvas.style.setProperty('--fic-crop-ratio', String(ratio));
      this.syncUpsellCanvasLayout(canvas, ratio);
      const bounds = canvas.getBoundingClientRect();
      let cssWidth = Math.max(1, bounds.width || canvas.parentElement?.clientWidth || 640);
      let cssHeight = Math.max(1, bounds.height || cssWidth / ratio);
      const displayedRatio = cssWidth / cssHeight;
      if (Math.abs(displayedRatio - ratio) > 0.001) {
        if (displayedRatio > ratio) cssWidth = Math.max(1, cssHeight * ratio);
        else cssHeight = Math.max(1, cssWidth / ratio);
      }
      const density = Math.min(window.devicePixelRatio || 1, 2);
      let width = Math.round(cssWidth * density);
      let height = Math.round(cssHeight * density);
      const longest = Math.max(width, height);
      if (longest > DISPLAY_MAX_DIMENSION) {
        const scale = DISPLAY_MAX_DIMENSION / longest;
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      return { width, height, cssWidth, cssHeight, density: width / cssWidth };
    }

    drawToCanvas(canvas) {
      if (!canvas || !this.image) return;
      const dimensions = this.prepareDisplayCanvas(canvas);
      if (!dimensions) return;
      const context = canvas.getContext('2d', { alpha: false });
      if (canvas === this.editCanvas && this.isMobileEditorLayout()) {
        this.drawMobileEditor(context, dimensions);
      } else {
        this.drawCrop(context, dimensions.width, dimensions.height);
      }
    }

    getEditorCropRect(width, height) {
      if (!this.isMobileEditorLayout()) return { x: 0, y: 0, width, height };
      const ratio = this.getCropRatio();
      const maxWidth = width * 0.68;
      const maxHeight = height * 0.72;
      let cropWidth = maxWidth;
      let cropHeight = cropWidth / ratio;
      if (cropHeight > maxHeight) {
        cropHeight = maxHeight;
        cropWidth = cropHeight * ratio;
      }
      return {
        x: (width - cropWidth) / 2,
        y: (height - cropHeight) / 2,
        width: cropWidth,
        height: cropHeight,
      };
    }

    drawMobileEditor(context, dimensions) {
      if (!context || !this.image) return;
      const { width, height, density, cssWidth, cssHeight } = dimensions;
      const crop = this.getEditorCropRect(width, height);
      const sourceWidth = Number(this.image.width || this.image.naturalWidth);
      const sourceHeight = Number(this.image.height || this.image.naturalHeight);
      const quarterTurn = Math.abs(this.state.rotation / 90) % 2 === 1;
      const rotatedWidth = quarterTurn ? sourceHeight : sourceWidth;
      const rotatedHeight = quarterTurn ? sourceWidth : sourceHeight;
      const scale = Math.max(crop.width / rotatedWidth, crop.height / rotatedHeight) * this.state.zoom;
      const focusX = this.state.focusX * sourceWidth;
      const focusY = this.state.focusY * sourceHeight;

      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, width, height);
      const checkerSize = Math.max(8, Math.round(12 * density));
      for (let y = 0; y < height; y += checkerSize) {
        for (let x = 0; x < width; x += checkerSize) {
          context.fillStyle = ((x / checkerSize + y / checkerSize) % 2 === 0)
            ? 'rgb(126, 126, 126)'
            : 'rgb(101, 101, 101)';
          context.fillRect(x, y, checkerSize, checkerSize);
        }
      }

      context.translate(crop.x + crop.width / 2, crop.y + crop.height / 2);
      context.scale(scale * (this.state.flipX ? -1 : 1), scale);
      context.rotate((this.state.rotation * Math.PI) / 180);
      context.translate(-focusX, -focusY);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(this.image, 0, 0, sourceWidth, sourceHeight);
      context.restore();

      context.save();
      context.fillStyle = 'rgba(0, 0, 0, 0.58)';
      context.fillRect(0, 0, width, crop.y);
      context.fillRect(0, crop.y + crop.height, width, height - crop.y - crop.height);
      context.fillRect(0, crop.y, crop.x, crop.height);
      context.fillRect(crop.x + crop.width, crop.y, width - crop.x - crop.width, crop.height);

      const computedStyle = getComputedStyle(this);
      const configuredAccent = computedStyle.getPropertyValue('--fic-mobile-crop-accent').trim();
      const computedAccent = String(computedStyle.accentColor || '').trim();
      const accent = configuredAccent || (computedAccent && computedAccent !== 'auto' ? computedAccent : 'Highlight');
      context.strokeStyle = accent;
      context.lineWidth = Math.max(2, 2 * density);
      context.setLineDash([]);
      context.strokeRect(crop.x, crop.y, crop.width, crop.height);

      context.strokeStyle = 'rgba(255, 255, 255, 0.72)';
      context.lineWidth = Math.max(1, density);
      context.setLineDash([5 * density, 5 * density]);
      for (const fraction of [1 / 3, 2 / 3]) {
        context.beginPath();
        context.moveTo(crop.x + crop.width * fraction, crop.y);
        context.lineTo(crop.x + crop.width * fraction, crop.y + crop.height);
        context.stroke();
        context.beginPath();
        context.moveTo(crop.x, crop.y + crop.height * fraction);
        context.lineTo(crop.x + crop.width, crop.y + crop.height * fraction);
        context.stroke();
      }

      context.fillStyle = accent;
      const handleSize = Math.max(8, 9 * density);
      const largeHandleSize = Math.max(16, 20 * density);
      const handles = [
        [crop.x, crop.y],
        [crop.x + crop.width / 2, crop.y],
        [crop.x + crop.width, crop.y],
        [crop.x, crop.y + crop.height / 2],
        [crop.x + crop.width, crop.y + crop.height / 2],
        [crop.x, crop.y + crop.height],
        [crop.x + crop.width / 2, crop.y + crop.height],
      ];
      handles.forEach(([x, y]) => {
        context.fillRect(x - handleSize / 2, y - handleSize / 2, handleSize, handleSize);
      });
      context.fillRect(
        crop.x + crop.width - largeHandleSize / 2,
        crop.y + crop.height - largeHandleSize / 2,
        largeHandleSize,
        largeHandleSize
      );
      context.restore();

      const cssCrop = this.getEditorCropRect(cssWidth, cssHeight);
      this.editCanvas.dataset.ficCropRect = [cssCrop.x, cssCrop.y, cssCrop.width, cssCrop.height]
        .map((value) => value.toFixed(2))
        .join(',');
    }

    drawCrop(context, width, height) {
      if (!context || !this.image) return;
      const sourceWidth = Number(this.image.width || this.image.naturalWidth);
      const sourceHeight = Number(this.image.height || this.image.naturalHeight);
      const quarterTurn = Math.abs(this.state.rotation / 90) % 2 === 1;
      const rotatedWidth = quarterTurn ? sourceHeight : sourceWidth;
      const rotatedHeight = quarterTurn ? sourceWidth : sourceHeight;
      const scale = Math.max(width / rotatedWidth, height / rotatedHeight) * this.state.zoom;
      const focusX = this.state.focusX * sourceWidth;
      const focusY = this.state.focusY * sourceHeight;

      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, width, height);
      context.fillStyle = this.getCanvasBackgroundColor();
      context.fillRect(0, 0, width, height);
      context.translate(width / 2, height / 2);
      context.scale(scale * (this.state.flipX ? -1 : 1), scale);
      context.rotate((this.state.rotation * Math.PI) / 180);
      context.translate(-focusX, -focusY);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(this.image, 0, 0, sourceWidth, sourceHeight);
      context.restore();
    }

    getCanvasBackgroundColor() {
      const colorChannels = getComputedStyle(this).getPropertyValue('--color-background').trim();
      return colorChannels ? `rgb(${colorChannels})` : 'Canvas';
    }

    getCropMetrics(targetWidth = 1000, targetHeight = targetWidth / this.getCropRatio()) {
      if (!this.image) return null;
      const sourceWidth = Number(this.image.width || this.image.naturalWidth);
      const sourceHeight = Number(this.image.height || this.image.naturalHeight);
      const quarterTurn = Math.abs(this.state.rotation / 90) % 2 === 1;
      const rotatedWidth = quarterTurn ? sourceHeight : sourceWidth;
      const rotatedHeight = quarterTurn ? sourceWidth : sourceHeight;
      const scale = Math.max(targetWidth / rotatedWidth, targetHeight / rotatedHeight) * this.state.zoom;
      const rotatedCropWidth = targetWidth / scale;
      const rotatedCropHeight = targetHeight / scale;
      return {
        sourceWidth,
        sourceHeight,
        scale,
        sourceCropWidth: quarterTurn ? rotatedCropHeight : rotatedCropWidth,
        sourceCropHeight: quarterTurn ? rotatedCropWidth : rotatedCropHeight,
      };
    }

    clampFocus() {
      const metrics = this.getCropMetrics();
      if (!metrics) return;
      const halfWidth = Math.min(metrics.sourceWidth / 2, metrics.sourceCropWidth / 2);
      const halfHeight = Math.min(metrics.sourceHeight / 2, metrics.sourceCropHeight / 2);
      const focusX = clamp(this.state.focusX * metrics.sourceWidth, halfWidth, metrics.sourceWidth - halfWidth);
      const focusY = clamp(this.state.focusY * metrics.sourceHeight, halfHeight, metrics.sourceHeight - halfHeight);
      this.state.focusX = focusX / metrics.sourceWidth;
      this.state.focusY = focusY / metrics.sourceHeight;
    }

    adjustZoom(delta, anchor = null) {
      if (!this.image) return;
      const oldZoom = this.state.zoom;
      this.state.zoom = clamp(this.state.zoom + delta, 1, 8);
      if (anchor && oldZoom !== this.state.zoom) this.zoomAroundPoint(anchor, oldZoom);
      this.clampFocus();
      this.markDirty();
      this.drawToCanvas(this.editCanvas);
    }

    zoomAroundPoint(anchor, oldZoom) {
      if (!this.editCanvas || !this.image) return;
      const bounds = this.editCanvas.getBoundingClientRect();
      const crop = this.getEditorCropRect(bounds.width || 1000, bounds.height || 1000 / this.getCropRatio());
      const anchorX = clamp(anchor.x - bounds.left, crop.x, crop.x + crop.width);
      const anchorY = clamp(anchor.y - bounds.top, crop.y, crop.y + crop.height);
      const relativeX = (anchorX - crop.x) / Math.max(1, crop.width) - 0.5;
      const relativeY = (anchorY - crop.y) / Math.max(1, crop.height) - 0.5;
      const metrics = this.getCropMetrics(crop.width, crop.height);
      if (!metrics) return;
      const change = 1 - oldZoom / this.state.zoom;
      const delta = this.screenDeltaToSource(
        relativeX * crop.width * change,
        relativeY * crop.height * change,
        metrics.scale
      );
      this.state.focusX += delta.x / metrics.sourceWidth;
      this.state.focusY += delta.y / metrics.sourceHeight;
    }

    rotateImage() {
      if (!this.image) return;
      this.state.rotation = (this.state.rotation + 90) % 360;
      this.clampFocus();
      this.markDirty();
      this.drawToCanvas(this.editCanvas);
    }

    flipImage() {
      if (!this.image) return;
      this.state.flipX = !this.state.flipX;
      this.markDirty();
      this.drawToCanvas(this.editCanvas);
    }

    fitImage() {
      if (!this.image) return;
      this.state.zoom = 1;
      this.state.focusX = 0.5;
      this.state.focusY = 0.5;
      this.clampFocus();
      this.markDirty();
      this.drawToCanvas(this.editCanvas);
    }

    handlePointerDown(event) {
      if (!this.image || event.button > 0) return;
      this.editCanvas.setPointerCapture(event.pointerId);
      this.pointerState.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointerState.size === 1) {
        this.activePointerMode = 'pan';
        this.lastPointerPoint = { x: event.clientX, y: event.clientY };
      } else if (this.pointerState.size === 2) {
        const points = Array.from(this.pointerState.values());
        this.activePointerMode = 'pinch';
        this.pinchStart = {
          distance: Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y),
          zoom: this.state.zoom,
        };
      }
      event.preventDefault();
    }

    handlePointerMove(event) {
      if (!this.pointerState.has(event.pointerId) || !this.image) return;
      this.pointerState.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointerState.size >= 2 && this.pinchStart) {
        const points = Array.from(this.pointerState.values());
        const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
        if (this.pinchStart.distance > 0) {
          const oldZoom = this.state.zoom;
          this.state.zoom = clamp(this.pinchStart.zoom * (distance / this.pinchStart.distance), 1, 8);
          if (oldZoom !== this.state.zoom) {
            this.zoomAroundPoint(
              { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 },
              oldZoom
            );
          }
          this.clampFocus();
          this.markDirty();
          this.drawToCanvas(this.editCanvas);
        }
      } else if (this.activePointerMode === 'pan' && this.lastPointerPoint) {
        const deltaX = event.clientX - this.lastPointerPoint.x;
        const deltaY = event.clientY - this.lastPointerPoint.y;
        this.panByScreenDelta(deltaX, deltaY);
        this.lastPointerPoint = { x: event.clientX, y: event.clientY };
      }
      event.preventDefault();
    }

    handlePointerUp(event) {
      this.pointerState.delete(event.pointerId);
      if (this.editCanvas?.hasPointerCapture(event.pointerId)) this.editCanvas.releasePointerCapture(event.pointerId);
      if (this.pointerState.size === 1) {
        const [point] = this.pointerState.values();
        this.activePointerMode = 'pan';
        this.lastPointerPoint = { ...point };
        this.pinchStart = null;
      } else if (this.pointerState.size === 0) {
        this.activePointerMode = '';
        this.lastPointerPoint = null;
        this.pinchStart = null;
      }
    }

    handleWheel(event) {
      if (!this.image) return;
      event.preventDefault();
      this.adjustZoom(event.deltaY < 0 ? 0.12 : -0.12, { x: event.clientX, y: event.clientY });
    }

    handleEditorKeydown(event) {
      if (!this.image) return;
      const step = event.shiftKey ? 30 : 10;
      const movements = {
        ArrowLeft: [step, 0],
        ArrowRight: [-step, 0],
        ArrowUp: [0, step],
        ArrowDown: [0, -step],
      };
      if (movements[event.key]) {
        event.preventDefault();
        this.panByScreenDelta(...movements[event.key]);
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        this.adjustZoom(0.15);
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        this.adjustZoom(-0.15);
      }
    }

    panByScreenDelta(deltaX, deltaY) {
      if (!this.image || !this.editCanvas) return;
      const bounds = this.editCanvas.getBoundingClientRect();
      const crop = this.getEditorCropRect(bounds.width || 1000, bounds.height || 1000 / this.getCropRatio());
      const metrics = this.getCropMetrics(crop.width, crop.height);
      if (!metrics) return;
      const delta = this.screenDeltaToSource(deltaX, deltaY, metrics.scale);
      this.state.focusX -= delta.x / metrics.sourceWidth;
      this.state.focusY -= delta.y / metrics.sourceHeight;
      this.clampFocus();
      this.markDirty();
      this.drawToCanvas(this.editCanvas);
    }

    screenDeltaToSource(deltaX, deltaY, scale) {
      let x = deltaX / Math.max(scale, Number.EPSILON);
      let y = deltaY / Math.max(scale, Number.EPSILON);
      if (this.state.flipX) x *= -1;
      const radians = (-this.state.rotation * Math.PI) / 180;
      return {
        x: Math.cos(radians) * x - Math.sin(radians) * y,
        y: Math.sin(radians) * x + Math.cos(radians) * y,
      };
    }

    scheduleWallPreview(delay = 120) {
      clearTimeout(this.wallRenderTimer);
      const token = ++this.wallRenderToken;
      if (!this.image || !this.wallArt) return;
      this.wallRenderTimer = window.setTimeout(() => this.renderWallPreview(token), delay);
    }

    async renderWallPreview(token) {
      if (!this.image || !this.wallArt) return;
      try {
        const canvas = this.createCropCanvas(900);
        const blob = await this.canvasToBlob(canvas, 'image/jpeg', 0.86);
        if (token !== this.wallRenderToken) return;
        this.revokeObjectUrl('wallObjectUrl');
        this.wallObjectUrl = URL.createObjectURL(blob);
        this.wallArt.src = this.wallObjectUrl;
        this.wallArt.hidden = false;
      } catch (error) {
        console.error('Afbeeldingsconfigurator: wandvoorbeeld kon niet worden gemaakt.', error);
      }
    }

    createCropCanvas(maxDimension) {
      const ratio = this.getCropRatio();
      const width = ratio >= 1 ? maxDimension : Math.max(1, Math.round(maxDimension * ratio));
      const height = ratio >= 1 ? Math.max(1, Math.round(maxDimension / ratio)) : maxDimension;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      this.drawCrop(canvas.getContext('2d', { alpha: false }), width, height);
      return canvas;
    }

    async showMosaicPreview() {
      if (!this.validateConfiguration(false)) return;
      this.setStep('preview');
      this.setBusy(true, this.labels.processing || 'Voorbeeld maken…');
      const token = ++this.previewRenderToken;

      try {
        await nextFrame();
        await this.renderMosaic(token);
        this.clearStatus();
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.error('Afbeeldingsconfigurator: voorbeeld kon niet worden gemaakt.', error);
          this.showStatus(this.labels.invalidFile || 'Het voorbeeld kon niet worden gemaakt.', true);
        }
      } finally {
        if (token === this.previewRenderToken) this.setBusy(false);
      }
    }

    async renderMosaic(token) {
      if (!this.previewCanvas || !this.image) return;
      const core = globalThis.FinchDiamondPreviewCore;
      if (!core) throw new Error('De diamond-previewkern is niet beschikbaar.');

      const selectedSize = this.getSelectedSize();
      const { width, height } = this.getSizeDimensions(selectedSize);
      const orientation = this.getSelectedOrientation()?.dataset.ficOrientation || this.orientation;
      const shape = this.getSelectedStone()?.dataset.ficStone || 'square';
      const grid = core.calculateGrid({
        widthCm: width,
        heightCm: height,
        orientation,
        shape,
        squarePitch: Number(this.settings.squareDrillPitch) || 2.5,
        roundPitch: Number(this.settings.roundDrillPitch) || 2.8,
      });
      const sourceLongEdge = clamp(Math.max(grid.columns, grid.rows) * 2, 320, 1200);
      const sourceCanvas = this.createCropCanvas(Math.round(sourceLongEdge));
      const sourceContext = sourceCanvas.getContext('2d', { alpha: false, willReadFrequently: true });
      const sourceImageData = sourceContext.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
      const maxOutputPixels = matchMedia('(max-width: 749px)').matches ? 1_500_000 : 2_500_000;
      const payload = {
        id: token,
        coreUrl: this.settings.diamondPreviewCoreUrl,
        sourceWidth: sourceCanvas.width,
        sourceHeight: sourceCanvas.height,
        widthCm: width,
        heightCm: height,
        orientation,
        shape,
        squarePitch: Number(this.settings.squareDrillPitch) || 2.5,
        roundPitch: Number(this.settings.roundDrillPitch) || 2.8,
        maxColors: clamp(
          Math.floor(Number(this.settings.previewMaxColors) || MAX_PREVIEW_BASE_COLORS),
          1,
          MAX_PREVIEW_BASE_COLORS
        ),
        maxOutputPixels,
        background: this.getPreviewBackgroundRgb(),
        seed: Number(this.config.product?.id || 0),
      };

      let result;
      try {
        result = await this.renderMosaicInWorker(
          { ...payload, sourceBuffer: new Uint8ClampedArray(sourceImageData.data).buffer },
          token
        );
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        console.warn('Afbeeldingsconfigurator: preview-worker niet beschikbaar; veilige fallback wordt gebruikt.', error);
        await yieldToBrowser();
        result = this.renderMosaicOnMainThread(core, payload, sourceImageData.data, grid);
        await yieldToBrowser();
      }

      if (token !== this.previewRenderToken) throw new DOMException('Preview vervangen', 'AbortError');
      this.commitMosaicPreview(result, token);
    }

    renderMosaicOnMainThread(core, payload, sourceData, grid) {
      const pattern = core.createPattern({
        data: sourceData,
        width: payload.sourceWidth,
        height: payload.sourceHeight,
        columns: grid.columns,
        rows: grid.rows,
        widthCm: grid.widthCm,
        heightCm: grid.heightCm,
        maxColors: payload.maxColors,
        highFidelity: true,
      });
      const rendered = core.renderPattern({
        indices: pattern.indices,
        palette: pattern.palette,
        cellRgba: pattern.cellRgba,
        columns: grid.columns,
        rows: grid.rows,
        shape: grid.stoneShape,
        background: payload.background,
        maxOutputPixels: payload.maxOutputPixels,
        seed: payload.seed,
      });
      return {
        data: rendered.data.buffer,
        width: rendered.width,
        height: rendered.height,
        cellSize: rendered.cellSize,
        grid,
        colorsUsed: pattern.colorsUsed,
        paletteMode: pattern.paletteMode,
        cleanupChanged: pattern.cleanupChanged,
        areaCoverageRatio: rendered.coverage,
      };
    }

    renderMosaicInWorker(payload, token) {
      const workerUrl = this.settings.diamondPreviewWorkerUrl;
      if (!workerUrl || !globalThis.Worker) return Promise.reject(new Error('preview-worker-unavailable'));
      this.cancelPreviewWorker();

      return new Promise((resolve, reject) => {
        let worker;
        try {
          worker = new Worker(new URL(workerUrl, document.baseURI).href);
        } catch (error) {
          reject(error);
          return;
        }
        this.previewWorker = worker;
        const timeout = window.setTimeout(() => {
          cleanup();
          reject(new Error('preview-worker-timeout'));
        }, 60_000);
        const cleanup = () => {
          clearTimeout(timeout);
          worker.terminate();
          if (this.previewWorker === worker) {
            this.previewWorker = null;
            this.previewWorkerReject = null;
          }
        };
        this.previewWorkerReject = (reason) => {
          cleanup();
          reject(reason);
        };
        worker.addEventListener('message', (event) => {
          const message = event.data || {};
          if (message.id !== token) return;
          cleanup();
          if (message.type === 'error') {
            reject(new Error(message.message || 'preview-worker-error'));
            return;
          }
          if (message.type !== 'rendered') {
            reject(new Error('preview-worker-invalid-response'));
            return;
          }
          resolve(message);
        });
        worker.addEventListener('error', (event) => {
          cleanup();
          reject(event.error || new Error(event.message || 'preview-worker-error'));
        });
        worker.postMessage({ type: 'render', ...payload }, [payload.sourceBuffer]);
      });
    }

    cancelPreviewWorker() {
      const worker = this.previewWorker;
      const reject = this.previewWorkerReject;
      this.previewWorker = null;
      this.previewWorkerReject = null;
      worker?.terminate();
      if (reject) reject(new DOMException('Preview vervangen', 'AbortError'));
    }

    getPreviewBackgroundRgb() {
      const channels = getComputedStyle(this).getPropertyValue('--color-background').trim().match(/[\d.]+/g);
      if (!channels || channels.length < 3) return { r: 245, g: 245, b: 245 };
      return {
        r: clamp(Math.round(Number(channels[0])), 0, 255),
        g: clamp(Math.round(Number(channels[1])), 0, 255),
        b: clamp(Math.round(Number(channels[2])), 0, 255),
      };
    }

    commitMosaicPreview(result, token) {
      const pixels = new Uint8ClampedArray(result.data);
      this.previewCanvas.width = result.width;
      this.previewCanvas.height = result.height;
      this.previewCanvas.style.aspectRatio = `${result.width} / ${result.height}`;
      const context = this.previewCanvas.getContext('2d', { alpha: false });
      context.putImageData(new ImageData(pixels, result.width, result.height), 0, 0);

      const metadata = Object.freeze({
        ...result.grid,
        colorCount: Number(result.colorsUsed || 0),
        paletteMode: result.paletteMode || 'clean-50',
        cellSize: Number(result.cellSize || 1),
        areaCoverageRatio: Number(result.areaCoverageRatio || 0),
        cleanupChanged: Number(result.cleanupChanged || 0),
        configurationVersion: this.configurationVersion,
        renderToken: token,
      });
      this.lastPreviewMetadata = metadata;
      this.previewCanvas.dataset.columns = String(metadata.columns);
      this.previewCanvas.dataset.rows = String(metadata.rows);
      this.previewCanvas.dataset.stoneShape = metadata.stoneShape;
      this.previewCanvas.dataset.drillPitchMm = String(metadata.drillPitchMm);
      this.previewCanvas.dataset.colorCount = String(metadata.colorCount);
      this.previewCanvas.dataset.paletteMode = metadata.paletteMode;
      this.previewCanvas.dataset.areaCoverageRatio = String(metadata.areaCoverageRatio);
      this.updatePreviewMetadata(metadata);
    }

    updatePreviewMetadata(metadata) {
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'nl-NL');
      const selectedSize = this.getSelectedSize();
      const selectedStone = this.getSelectedStone();
      if (this.previewSelection) {
        this.previewSelection.textContent = [selectedSize?.value, selectedStone?.value].filter(Boolean).join(' · ');
      }
      if (this.previewDrills) this.previewDrills.textContent = formatter.format(metadata.totalDrills);
      if (this.previewMeta) this.previewMeta.hidden = false;
    }

    validateConfiguration(focusInvalid = true) {
      if (this.sourceLoading) {
        this.showStatus(this.labels.processing || 'Wacht tot je afbeelding is verwerkt.', false);
        return false;
      }
      if (!this.image) {
        this.showStatus(this.labels.requiredUpload || 'Upload eerst een foto.', true);
        this.setStep('intro');
        if (focusInvalid) this.querySelector('[data-fic-action="upload"]')?.focus();
        return false;
      }
      const selectedSize = this.getSelectedSize();
      const selectedOrientation = this.getSelectedOrientation();
      const selectedOrientationKey = selectedOrientation?.dataset.ficOrientation || this.orientation;
      const selectedVariant = this.getVariantForSize(selectedSize);
      if (
        !selectedSize ||
        !selectedOrientation ||
        selectedSize.disabled ||
        !selectedVariant?.available ||
        !this.isSizeCompatible(selectedSize, selectedOrientationKey)
      ) {
        this.showStatus(this.labels.requiredSize || 'Kies een beschikbare afmeting voor dit formaat.', true);
        this.setConfigureGuide('size');
        this.setStep('configure');
        if (focusInvalid) this.querySelector('[data-fic-orientation]:checked')?.focus();
        return false;
      }
      if (!this.getSelectedStone()) {
        this.showStatus(this.labels.requiredStone || 'Kies een steenvorm.', true);
        this.setConfigureGuide('stone');
        this.setStep('configure');
        if (focusInvalid) this.querySelector('[data-fic-stone]')?.focus();
        return false;
      }
      return true;
    }

    async prepareConfiguration(button) {
      if (!this.validateConfiguration()) return;
      const version = this.configurationVersion;
      const sourceToken = this.sourceLoadToken;
      const finalizeToken = ++this.finalizeToken;
      this.setBusy(true, this.labels.processing || 'Afbeelding verwerken…');
      if (button) button.disabled = true;

      try {
        this.updateVariantSelection(true);
        const exportCanvas = this.createExportCanvas();
        const quality = clamp(Number(this.settings.outputQuality) || DEFAULT_OUTPUT_QUALITY, 0.5, 1);
        const blob = await this.canvasToBlob(exportCanvas, 'image/jpeg', quality);
        if (
          version !== this.configurationVersion ||
          sourceToken !== this.sourceLoadToken ||
          finalizeToken !== this.finalizeToken ||
          this.sourceLoading
        ) {
          throw new DOMException('Configuratie gewijzigd', 'AbortError');
        }
        const baseName = this.sourceFile?.name?.replace(/\.[^.]+$/, '') || 'upload';
        const safeName = baseName.replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '') || 'upload';
        this.generatedFile = new File([blob], `${safeName}-uitsnede.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        this.assignGeneratedFile();
        this.writeCropMetadata();
        this.updateReadyState(true);
        this.clearStatus();
        this.dispatchEvent(
          new CustomEvent('fic:configured', {
            bubbles: true,
            detail: {
              file: this.generatedFile,
              variantId: this.getSelectedSize()?.dataset.variantId,
              orientation: this.orientation,
              stone: this.getSelectedStone()?.value,
            },
          })
        );
        return true;
      } catch (error) {
        if (error?.name === 'AbortError') {
          if (this.dialog?.open) {
            this.showStatus(
              this.labels.processing || 'De configuratie is gewijzigd. Controleer je keuzes en ga opnieuw verder.',
              false
            );
          }
        } else {
          console.error('Afbeeldingsconfigurator: exporteren is mislukt.', error);
          this.showStatus(this.labels.invalidFile || 'De afbeelding kon niet worden verwerkt. Probeer het opnieuw.', true);
        }
        return false;
      } finally {
        this.setBusy(false);
        if (button) button.disabled = false;
      }
    }

    async finalizeConfiguration(button) {
      return this.prepareConfiguration(button);
    }

    createPurchaseState() {
      const randomId = globalThis.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return {
        id: `fic-${randomId}`,
        mainAdded: false,
        addedUpsellVariantIds: new Set(),
        lastResponse: null,
      };
    }

    getCartSections() {
      if (!this.cart?.getSectionsToRender) return [];
      return this.cart.getSectionsToRender().map((section) => section.id);
    }

    async requestCartAdd(body, { json = false } = {}) {
      const headers = {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      };
      if (json) headers['Content-Type'] = 'application/json';

      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers,
        credentials: 'same-origin',
        body: json ? JSON.stringify(body) : body,
      });

      let data;
      try {
        data = await response.json();
      } catch (_error) {
        throw new Error(this.labels.invalidCartResponse || 'Shopify gaf geen geldig antwoord.');
      }

      if (!response.ok || data.status || data.errors) {
        const errors = typeof data.errors === 'string' ? data.errors : null;
        throw new Error(
          errors || data.description || data.message || this.labels.addFailed || 'Toevoegen aan de winkelwagen is mislukt.'
        );
      }
      return data;
    }

    async executePurchaseTransaction() {
      if (!this.form) throw new Error(this.labels.addFailed || 'Het productformulier ontbreekt.');
      const sections = this.getCartSections();
      let response = this.purchaseState.lastResponse;

      if (!this.purchaseState.mainAdded) {
        this.updateVariantSelection(true);
        const formData = new FormData(this.form);
        const selectedVariantId = this.getSelectedSize()?.dataset.variantId;
        const selectedOrientation = this.getSelectedOrientation();
        const selectedStone = this.getSelectedStone();
        if (selectedVariantId) formData.set('id', selectedVariantId);
        if (selectedOrientation?.name) formData.set(selectedOrientation.name, selectedOrientation.value);
        if (selectedStone?.name) formData.set(selectedStone.name, selectedStone.value);
        formData.set('properties[_finch_configuration_id]', this.purchaseState.id);
        if (sections.length) formData.set('sections', sections.join(','));
        formData.set('sections_url', window.location.pathname);
        response = await this.requestCartAdd(formData);
        this.purchaseState.mainAdded = true;
        this.purchaseState.lastResponse = response;
      }

      const pendingUpsells = this.getSelectedUpsellItems().filter(
        (item) => !this.purchaseState.addedUpsellVariantIds.has(item.id)
      );
      if (pendingUpsells.length) {
        response = await this.requestCartAdd(
          {
            items: pendingUpsells.map((item) => ({
              id: item.id,
              quantity: item.quantity,
              properties: {
                _finch_configuration_id: this.purchaseState.id,
                _finch_configurator_addon: 'true',
              },
            })),
            sections,
            sections_url: window.location.pathname,
          },
          { json: true }
        );
        pendingUpsells.forEach((item) => this.purchaseState.addedUpsellVariantIds.add(item.id));
        this.purchaseState.lastResponse = response;
      }

      return response;
    }

    async commitPurchase(mode, button) {
      if (this.purchaseInFlight || !this.validateConfiguration()) return;
      this.purchaseInFlight = true;

      try {
        if (!this.generatedFile || this.dataset.ready !== 'true') {
          const prepared = await this.prepareConfiguration(button);
          if (!prepared) return;
        }

        this.setBusy(true, this.labels.addingToCart || 'Je ontwerp wordt toegevoegd…');
        if (button) button.disabled = true;
        this.cart?.setActiveElement?.(button || this.lastFocusedElement);

        const task = () => this.executePurchaseTransaction();
        const response = this.cart?.enqueueCartMutation
          ? await this.cart.enqueueCartMutation(task)
          : await task();

        if (
          typeof publish === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.cartUpdate
        ) {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'finch-configurator',
            productVariantId: this.getSelectedSize()?.dataset.variantId,
            cartData: response,
          });
        }

        const resumeBuyNow = this.pendingBuyNow && mode === 'cart';
        if (this.cart && response?.sections) {
          this.cart.classList.remove('is-empty');
          this.cart.renderContents(response, { openDrawer: mode === 'cart' && !resumeBuyNow });
        }

        if (mode === 'another') {
          this.resetForAnotherPhoto();
          this.showStatus(this.labels.addedAnother || 'Je ontwerp is toegevoegd. Upload nu de volgende foto.', false);
          requestAnimationFrame(() => {
            const uploadButton = this.querySelector('[data-fic-step="intro"] [data-fic-action="upload"]');
            uploadButton?.focus({ preventScroll: true });
            this.sourceInput?.click();
          });
          return;
        }

        this.pendingSubmitter = null;
        this.pendingBuyNow = false;
        this.close();
        this.resetConfiguration();
        if (resumeBuyNow) {
          window.location.assign(window.routes?.checkout_url || '/checkout');
        } else if (!this.cart) {
          window.location.assign(window.routes?.cart_url || '/cart');
        }
      } catch (error) {
        console.error('Afbeeldingsconfigurator: winkelwagenactie is mislukt.', error);
        this.setStep('upsell-services');
        this.showStatus(error.message || this.labels.addFailed || 'Toevoegen aan de winkelwagen is mislukt.', true);
      } finally {
        this.purchaseInFlight = false;
        this.setBusy(false);
        if (button) button.disabled = false;
      }
    }

    resetForAnotherPhoto() {
      this.pendingSubmitter = null;
      this.pendingBuyNow = false;
      this.resetConfiguration();
    }

    resetConfiguration({ preserveStep = false } = {}) {
      this.sourceLoadToken += 1;
      this.finalizeToken += 1;
      this.previewRenderToken += 1;
      this.cancelSourceDecode();
      this.cancelPreviewWorker();
      this.releaseDecodedImage();
      this.revokeObjectUrl('sourceObjectUrl');
      this.revokeObjectUrl('wallObjectUrl');
      this.image = null;
      this.sourceFile = null;
      this.generatedFile = null;
      this.lastPreviewMetadata = null;
      this.setConfigureGuide('size');
      this.editorSnapshot = null;
      this.sourceInput && (this.sourceInput.value = '');
      this.filePropertyInput && (this.filePropertyInput.value = '');
      this.cropPropertyInput && (this.cropPropertyInput.value = '');
      this.uploadPreview?.removeAttribute('src');
      if (this.uploadName) this.uploadName.textContent = '';
      if (this.uploadConfirmation) this.uploadConfirmation.hidden = true;
      if (this.previewMeta) this.previewMeta.hidden = true;
      this.removeAttribute('data-has-source');
      this.state = { focusX: 0.5, focusY: 0.5, zoom: 1, rotation: 0, flipX: false };
      this.configurationVersion += 1;
      this.updateReadyState(false);
      this.purchaseState = this.createPurchaseState();
      this.resetUpsellSelections();
      this.showView('product');
      if (!preserveStep) this.setStep('intro');
    }

    createExportCanvas() {
      const ratio = this.getCropRatio();
      const requestedMaximum = Math.max(512, Number(this.settings.outputMaxDimension) || 4096);
      const metrics = this.getCropMetrics();
      const availableLongEdge = Math.max(metrics?.sourceCropWidth || requestedMaximum, metrics?.sourceCropHeight || requestedMaximum);
      const maximum = Math.max(1, Math.round(Math.min(requestedMaximum, availableLongEdge)));
      const width = ratio >= 1 ? maximum : Math.max(1, Math.round(maximum * ratio));
      const height = ratio >= 1 ? Math.max(1, Math.round(maximum / ratio)) : maximum;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      this.drawCrop(canvas.getContext('2d', { alpha: false }), width, height);
      return canvas;
    }

    canvasToBlob(canvas, type, quality) {
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('canvas-to-blob'));
        }, type, quality);
      });
    }

    assignGeneratedFile() {
      if (!this.filePropertyInput || !this.generatedFile) return;
      try {
        const transfer = new DataTransfer();
        transfer.items.add(this.generatedFile);
        this.filePropertyInput.files = transfer.files;
      } catch (error) {
        // The formdata listener below is the standards-based fallback.
      }
    }

    writeCropMetadata() {
      if (!this.cropPropertyInput || !this.image) return;
      const selectedSize = this.getSelectedSize();
      const preview =
        this.lastPreviewMetadata?.configurationVersion === this.configurationVersion
          ? {
              grid: [this.lastPreviewMetadata.columns, this.lastPreviewMetadata.rows],
              drills: this.lastPreviewMetadata.totalDrills,
              pitchMm: this.lastPreviewMetadata.drillPitchMm,
              colors: this.lastPreviewMetadata.colorCount,
            }
          : null;
      this.cropPropertyInput.value = JSON.stringify({
        version: 1,
        focus: [Number(this.state.focusX.toFixed(6)), Number(this.state.focusY.toFixed(6))],
        zoom: Number(this.state.zoom.toFixed(4)),
        rotation: this.state.rotation,
        flipX: this.state.flipX,
        source: [Number(this.image.width || this.image.naturalWidth), Number(this.image.height || this.image.naturalHeight)],
        size: selectedSize?.value || '',
        orientation: this.getSelectedOrientation()?.value || this.orientation,
        stone: this.getSelectedStone()?.value || '',
        preview,
      });
    }

    handleFormData(event) {
      if (!this.generatedFile || !this.filePropertyInput?.name) return;
      event.formData.set(this.filePropertyInput.name, this.generatedFile, this.generatedFile.name);
    }

    handleConfiguratorRequired(event) {
      if (event.detail?.source === 'buy-now') this.pendingBuyNow = true;
      this.open();
    }

    handleFormSubmit(event) {
      this.disableLegacyUploadKitForProduct();
      const required = this.dataset.required === 'true' || this.settings.required === true;
      const needsUpload = required && !this.image;
      const needsConfiguratorFlow = Boolean(this.image);
      if (!needsUpload && !needsConfiguratorFlow) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.pendingSubmitter = event.submitter || null;
      this.open({ pendingSubmitter: this.pendingSubmitter });
      if (needsUpload) this.showStatus(this.labels.requiredUpload || 'Upload eerst een foto.', true);
    }

    markDirty() {
      this.configurationVersion += 1;
      this.finalizeToken += 1;
      this.generatedFile = null;
      this.lastPreviewMetadata = null;
      this.cancelPreviewWorker();
      if (this.previewMeta) this.previewMeta.hidden = true;
      if (this.filePropertyInput) this.filePropertyInput.value = '';
      this.updateReadyState(false);
      this.previewRenderToken += 1;
      this.scheduleWallPreview();
    }

    updateReadyState(ready) {
      this.dataset.ready = String(Boolean(ready));
    }

    setBusy(isBusy, message = '') {
      this.toggleAttribute('aria-busy', isBusy);
      this.dialog?.toggleAttribute('aria-busy', isBusy);
      this.querySelectorAll(
        '[data-fic-upload-input], [data-fic-orientation], [data-fic-size], [data-fic-stone], [data-fic-upsell-toggle], [data-fic-action]:not([data-fic-action="close"])'
      ).forEach((control) => {
        if (isBusy && !control.disabled) {
          control.disabled = true;
          control.dataset.ficBusyDisabled = 'true';
        } else if (!isBusy && control.dataset.ficBusyDisabled === 'true') {
          control.disabled = false;
          delete control.dataset.ficBusyDisabled;
        }
      });
      if (isBusy && message) this.showStatus(message, false);
    }

    showStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = !message;
      this.status.classList.toggle('fic__status--error', isError);
      this.status.setAttribute('role', isError ? 'alert' : 'status');

      const upsellError = this.querySelector('[data-fic-upsell-error]');
      if (upsellError) {
        const showInUpsellPanel = Boolean(message && isError && /^upsell-/.test(this.currentStepName || ''));
        upsellError.textContent = showInUpsellPanel ? message : '';
        upsellError.hidden = !showInUpsellPanel;
        upsellError.setAttribute('role', showInUpsellPanel ? 'alert' : 'status');
      }
    }

    clearStatus() {
      if (!this.status) return;
      this.status.textContent = '';
      this.status.hidden = true;
      this.status.classList.remove('fic__status--error');
      this.status.setAttribute('role', 'status');
      const upsellError = this.querySelector('[data-fic-upsell-error]');
      if (upsellError) {
        upsellError.textContent = '';
        upsellError.hidden = true;
        upsellError.setAttribute('role', 'status');
      }
    }
  }

  customElements.define(ELEMENT_NAME, FinchImageConfigurator);
})();
